import { type FC, useState } from "react";

import { Mail } from "lucide-react";

import { PeopleSelector } from "@/shared/components/PeopleSelector";
import { Button } from "@/shared/components/ui/button";
import { FieldError } from "@/shared/components/ui/FieldError";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Separator } from "@/shared/components/ui/separator";
import { useUser } from "@/shared/hooks/useAppContext";
import { useDeceasedUserData } from "@/shared/hooks/useDeceasedUserData";
import { usePersonInvitations } from "@/shared/hooks/useEstateInvitations";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { useWillStepAnswers } from "@/shared/hooks/useWillStepAnswers";
import { contactFullName, isProfessionalContact } from "@/shared/utils/contactUtils";
import { splitName } from "@/shared/utils/names";
import { validateInviteForm } from "@/shared/utils/validators";

import { LegalAuthorityStepLayout } from "./LegalAuthorityStepLayout";

interface LegalAuthorityAttorneyInviteStepProps {
  personFirstName: string;
  estateId: number;
  onBack: () => void;
  onContinue: () => void;
}

export const LegalAuthorityAttorneyInviteStep: FC<LegalAuthorityAttorneyInviteStepProps> = ({
  personFirstName,
  estateId,
  onBack,
  onContinue,
}) => {
  const personName = personFirstName || "your loved one";
  const user = useUser();
  const { fullName: decedentFullName } = useDeceasedUserData();
  const { contacts, saveContactAsync } = useUserContacts();
  const { sendInvitation, isSendingInvitation } = usePersonInvitations(estateId);
  const { saveAnswers } = useWillStepAnswers(estateId);

  const inviterName = user?.first_name || "Someone";

  const estateName = decedentFullName || `${personName}'s`;

  const [contactId, setContactId] = useState<number | null>(null);
  const [fullName, setFullName] = useState("");
  const [firmName, setFirmName] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<{ fullName?: string; email?: string }>({});

  const pickContact = (id: number | null) => {
    const contact = contacts.find((c) => c.id === id);
    setContactId(id);
    setFullName(contactFullName(contact));
    setFirmName(contact?.company_or_firm ?? "");
    setEmail(contact?.email ?? "");
    setErrors({});
  };

  const handleSendInvite = async () => {
    const next = validateInviteForm(fullName, email);
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    const { firstName, surName: lastName } = splitName(fullName);

    try {
      await saveContactAsync(contactId, {
        first_name: firstName,
        last_name: lastName,
        email: email.trim().toLowerCase(),
        relationship: "attorney",
        company_or_firm: firmName.trim() || null,
      });

      await sendInvitation.mutateAsync({
        estateId,
        email: email.trim().toLowerCase(),
        role: "admin",
        isAttorney: true,
      });

      await saveAnswers({
        "attorney-invite": {
          name: fullName.trim(),
          firmName: firmName.trim() || null,
          email: email.trim().toLowerCase(),
        },
      });

      onContinue();
    } catch {
      // hooks handle snackbar errors
    }
  };

  return (
    <LegalAuthorityStepLayout
      title="Add your attorney to the estate"
      description="We'll send a secure link so they can see the estate record and help you fill it out. You control what's shared, and you can remove access anytime."
      activeStep={2}
      onBack={onBack}
      actionButton={
        <Button variant="brand" size="md" onClick={handleSendInvite} disabled={isSendingInvitation}>
          Send invite
        </Button>
      }
    >
      <Separator />

      <PeopleSelector
        mode="single"
        label="Choose from people"
        placeholder="Choose an advisor already in People"
        value={contactId}
        onChange={pickContact}
        filter={isProfessionalContact}
      />

      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="flex flex-1 flex-col gap-1.5">
          <Label>Full name</Label>
          <Input
            value={fullName}
            onChange={(e) => {
              setFullName(e.target.value);
              setErrors((prev) => ({ ...prev, fullName: undefined }));
            }}
            placeholder="Enter full name"
            className={errors.fullName ? "border-error" : ""}
          />
          <FieldError message={errors.fullName} />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <Label>
            Firm name <span className="text-tertiary">(optional)</span>
          </Label>
          <Input
            value={firmName}
            onChange={(e) => setFirmName(e.target.value)}
            placeholder="Doe & Associates LLP"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>Email</Label>
        <Input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setErrors((prev) => ({ ...prev, email: undefined }));
          }}
          placeholder="example@email.com"
          className={errors.email ? "border-error" : ""}
        />
        <FieldError message={errors.email} />
      </div>

      <div className="overflow-hidden rounded-lg border border-primary">
        <div className="flex items-center gap-2 bg-subtle px-4 py-3">
          <Mail className="h-4 w-4 text-secondary" aria-hidden="true" />
          <p className="text-sm text-secondary">Here's the invitation we'll send</p>
        </div>
        <Separator />
        <div className="flex flex-col gap-3 bg-white p-4 text-sm">
          <p>
            {inviterName} invited you to {estateName} estate on Elayne.
          </p>
          <p>
            Elayne is where families build the estate record: documents, contacts, and details,
            collected and organized for their attorney to review. Elayne handles information
            gathering, documents, IRS transcript access, and admin tasks, so you can stay focused on
            legal work.
          </p>
          <p>
            {personName}'s estate record is already started, with documents, contacts, and intake
            details organized and ready for you.
          </p>
          <p className="font-medium text-link underline">Join {estateName} estate</p>
        </div>
      </div>
    </LegalAuthorityStepLayout>
  );
};
